/**
 * Tabs over the tutor chat: the main conversation, and side conversations
 * branched from a passage.
 *
 * Reading an answer, the student selects the words they did not follow and
 * asks about them in a new tab; the main conversation is left exactly where
 * it was. A tab can branch again, so the tabs of a module form a tree (kept
 * by the backend, sessions/threads.py). Every tab is a conversation of its
 * own on the tutor: its own short-term memory, the passage it branched from
 * pinned into every prompt, and the tree's long-term memory shared.
 *
 * Getting back: a tab's header says what it branched from — click it, or a
 * crumb in the trail, and the parent tab opens scrolled to those very words.
 * Chips under an answer list the tabs branched from it. The tree button shows
 * every tab of the module, open or closed; closing a tab hides it, nothing
 * is lost. Which tabs are open is kept on the server, so the same tabs are
 * there on the phone tomorrow.
 *
 * api: TutorChat's { ask, job, history, clear, status } plus
 *      { threads(), createThread(body), updateThread(id, patch), askThread(id, body), threadHistory(id, before) }
 * mainRef: a ref to the main conversation's TutorChat (its imperative handle), for a host that feeds it
 * The rest of the props are TutorChat's.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, TextField, Tooltip, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import TutorChat from "./TutorChat";
import { errorMessage } from "../ContentBank/contentBankApi";
import { FONT } from "../Sessions/sessionParts";

const MAIN = "main";
const tabKey = (id) => `tutor-tab:${id}`;

const readTab = (id) => { try { return sessionStorage.getItem(tabKey(id)); } catch { return null; } };
const writeTab = (id, value) => { try { sessionStorage.setItem(tabKey(id), String(value)); } catch { /* not remembered */ } };

const ago = (iso) => {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
};

/** The branches from the main conversation down to `id`, oldest first. */
export function chainTo(threads, id) {
  const byId = new Map(threads.map((t) => [t.id, t]));
  const out = [];
  let t = byId.get(id);
  const seen = new Set();
  while (t && !seen.has(t.id)) {
    out.unshift(t);
    seen.add(t.id);
    t = t.parent ? byId.get(t.parent) : null;
  }
  return out;
}

/** The tree, flattened for a list: [{thread, depth}], children under their parent, oldest first. */
export function flatten(threads) {
  const kids = new Map();
  const ids = new Set(threads.map((t) => t.id));
  threads.forEach((t) => {
    const k = t.parent && ids.has(t.parent) ? t.parent : MAIN;   // a parent gone (trashed in admin): under the main conversation
    if (!kids.has(k)) kids.set(k, []);
    kids.get(k).push(t);
  });
  const out = [];
  const walk = (key, depth) => {
    (kids.get(key) || []).sort((a, b) => a.id - b.id).forEach((t) => { out.push({ thread: t, depth }); walk(t.id, depth + 1); });
  };
  walk(MAIN, 0);
  return out;
}

export default function TutorTabs({ api, pendingId, context, onSource, isClickable, labels = {}, quote = null, onClearQuote,
  height = { xs: 560, md: "100%" }, mainRef = null }) {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(MAIN);
  const [mounted, setMounted] = useState(() => new Set());
  const [unread, setUnread] = useState(() => new Set());
  const [treeAt, setTreeAt] = useState(null);
  const [renaming, setRenaming] = useState(null);     // {id, title}
  const [error, setError] = useState("");
  const refs = useRef({});
  const apiRef = useRef(api);
  apiRef.current = api;
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const byId = useMemo(() => new Map(threads.map((t) => [t.id, t])), [threads]);
  const openTabs = useMemo(() => threads.filter((t) => t.open).sort((a, b) => a.position - b.position || a.id - b.id), [threads]);
  const current = active === MAIN ? null : byId.get(active) || null;

  const activate = useCallback((key) => {
    setActive(key);
    setUnread((cur) => { if (!cur.has(key)) return cur; const next = new Set(cur); next.delete(key); return next; });
    setMounted((cur) => (key === MAIN || cur.has(key) ? cur : new Set(cur).add(key)));
    writeTab(pendingId, key);
  }, [pendingId]);

  // The tree, and the tab that was open last time.
  useEffect(() => {
    let on = true;
    Promise.resolve().then(() => apiRef.current.threads()).then((rows) => {
      if (!on) return;
      setThreads(rows || []);
      const saved = readTab(pendingId);
      const id = saved && saved !== MAIN ? Number(saved) : null;
      if (id && (rows || []).some((t) => t.id === id && t.open)) activate(id);
    }).catch((e) => { if (on) setError(errorMessage(e, "Couldn't load your tabs")); });
    return () => { on = false; };
  }, [pendingId, activate]);

  // A passage selected in the book goes to the main conversation's composer: bring it forward.
  useEffect(() => { if (quote?.text) activate(MAIN); }, [quote?.text, activate]);

  const upsert = (t) => setThreads((cur) => (cur.some((x) => x.id === t.id) ? cur.map((x) => (x.id === t.id ? { ...x, ...t } : x)) : [...cur, t]));

  const patch = async (id, changes) => {
    setThreads((cur) => cur.map((t) => (t.id === id ? { ...t, ...changes } : t)));   // at once; the server follows
    try {
      const row = await apiRef.current.updateThread(id, changes);
      if (alive.current && row) upsert(row);
    } catch (e) {
      if (alive.current) setError(errorMessage(e, "Couldn't save that tab"));
    }
  };

  const branching = useRef(false);
  /** Branch a new tab from `parentKey` at `anchor` ({quote, prefix, suffix, around, parent_turn}). */
  const branch = async (parentKey, anchor, extra = {}) => {
    if (branching.current) return;                      // a double click is one tab
    branching.current = true;
    setError("");
    try {
      const t = await apiRef.current.createThread({
        parent: parentKey === MAIN ? null : parentKey, parent_turn: anchor.parent_turn ?? null,
        kind: extra.kind || "answer", quote: anchor.quote, prefix: anchor.prefix || "", suffix: anchor.suffix || "",
        around: anchor.around || "", ...(extra.chapter_index !== undefined ? { chapter_index: extra.chapter_index } : {}),
        ...(extra.section ? { section: extra.section } : {}),
      });
      if (!alive.current) return;
      upsert(t);
      activate(t.id);
      setTimeout(() => refs.current[t.id]?.focus?.(), 80);
    } catch (e) {
      if (alive.current) setError(errorMessage(e, "Couldn't open a tab for that"));
    } finally {
      branching.current = false;
    }
  };

  const onQuoteBranch = quote?.text ? async () => {
    await branch(MAIN, { quote: quote.text, prefix: quote.prefix, suffix: quote.suffix, around: quote.around || quote.text },
      { kind: "book", chapter_index: Number.isInteger(quote.chapter) ? quote.chapter : undefined, section: quote.section || "" });
    if (onClearQuote) onClearQuote();
  } : undefined;

  const openThread = (id) => {
    const t = byId.get(id);
    if (!t) return;
    if (!t.open) patch(id, { open: true });
    activate(id);
  };

  const closeTab = (id) => {
    patch(id, { open: false });
    // Its chat is unmounted (a question still waiting is picked up again when it reopens).
    setMounted((cur) => { const next = new Set(cur); next.delete(id); return next; });
    if (active === id) {
      const t = byId.get(id);
      const parent = t?.parent && byId.get(t.parent)?.open ? t.parent : MAIN;
      activate(parent);
    }
  };

  /** Show where tab `t` branched from: its parent tab, scrolled to the words. */
  const reveal = (t) => {
    const parentKey = t.parent || MAIN;
    if (parentKey !== MAIN && !byId.get(parentKey)?.open) patch(parentKey, { open: true });
    activate(parentKey);
    if (t.kind === "book") {
      if (onSource) onSource({ chapter_index: t.chapter_index, section: t.section, quote: t.quote });
      return;
    }
    let tries = 0;
    const go = () => {
      const chat = refs.current[parentKey];
      if (chat?.revealTurn) chat.revealTurn(t.parent_turn, t.quote);
      else if (tries++ < 10) setTimeout(go, 60);
    };
    setTimeout(go, 60);
  };

  const trail = current ? chainTo(threads, current.id) : [];
  const parentLabel = (t) => (t.kind === "book" ? "the book" : t.parent ? `“${byId.get(t.parent)?.title || "a tab"}”` : "the main conversation");

  const threadApi = (t) => ({
    ask: (body) => apiRef.current.askThread(t.id, body),
    job: (token) => apiRef.current.job(token),
    history: (before) => apiRef.current.threadHistory(t.id, before),
    status: () => apiRef.current.status(),
  });
  const mainApi = useMemo(() => ({
    ...api,
    clear: api.clear ? async () => {
      const out = await api.clear();
      // Clearing the main conversation archives its tabs (the server did; follow it here).
      setThreads([]);
      setActive(MAIN);
      writeTab(pendingId, MAIN);
      return out;
    } : undefined,
  }), [api, pendingId]);

  const markUnread = (key) => { if (key !== active) setUnread((cur) => new Set(cur).add(key)); };
  const tree = flatten(threads);

  const keys = [MAIN, ...openTabs.map((t) => t.id)];
  const onTabKey = (e, key) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); activate(key); return; }
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const n = keys.indexOf(key);
    const next = keys[(n + (e.key === "ArrowRight" ? 1 : keys.length - 1)) % keys.length];
    activate(next);
    e.currentTarget.parentElement?.querySelector(`[data-tab="${next}"]`)?.focus();
  };

  const tab = (key, title, extra) => {
    const on = active === key;
    return (
      <Box key={key} role="tab" aria-selected={on} tabIndex={on ? 0 : -1} data-tab={key} onClick={() => activate(key)}
        onKeyDown={(e) => onTabKey(e, key)} title={title}
        sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1.25, height: 34, flexShrink: 0, cursor: "pointer",
          borderBottom: on ? "2px solid #0172A6" : "2px solid transparent", color: on ? "#0172A6" : "#4b5563",
          "&:hover": { backgroundColor: "#f8fafc" }, maxWidth: 190 }}>
        {key === MAIN && <ChatBubbleOutlineIcon sx={{ fontSize: 15 }} />}
        <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: on ? 600 : 500, whiteSpace: "nowrap", overflow: "hidden",
          textOverflow: "ellipsis" }}>{title}</Typography>
        {unread.has(key) && <Box data-testid="tutor-unread" sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: "#0172A6", flexShrink: 0 }} />}
        {extra}
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height, minHeight: 0 }}>
      <Box role="tablist" aria-label="Conversations" data-testid="tutor-tabs" sx={{ display: "flex", alignItems: "center", borderBottom: "1px solid #eef0f2", pl: 0.5,
        overflowX: "auto", scrollbarWidth: "thin", "&::-webkit-scrollbar": { height: 4 } }}>
        {tab(MAIN, labels.rootTab || "Chat")}
        {openTabs.map((t) => tab(t.id, t.title, (
          <IconButton size="small" aria-label={`Close tab ${t.title}`} onClick={(e) => { e.stopPropagation(); closeTab(t.id); }}
            sx={{ p: 0.1, ml: -0.25, color: "inherit", opacity: 0.7, "&:hover": { opacity: 1 } }}>
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )))}
        <Box sx={{ flex: 1 }} />
        {threads.length > 0 && (
          <Tooltip title="All tabs of this module">
            <IconButton size="small" aria-label="All tabs" onClick={(e) => setTreeAt(e.currentTarget)} sx={{ mx: 0.5, flexShrink: 0 }}>
              <AccountTreeOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {current && (
        <Box data-testid="tutor-trail" sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", px: 1.5, py: 0.4, gap: 0.25,
          borderBottom: "1px solid #eef0f2", backgroundColor: "#fafbfc" }}>
          <Button size="small" onClick={() => reveal(trail[0])} sx={{ textTransform: "none", fontFamily: FONT, fontSize: 12, minWidth: 0, px: 0.75, py: 0 }}>
            Main
          </Button>
          {trail.map((t, k) => (
            <React.Fragment key={t.id}>
              <ChevronRightIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
              {k === trail.length - 1 ? (
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: "#111827", fontWeight: 600, px: 0.5, maxWidth: 200,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.title}</Typography>
              ) : (
                <Button size="small" onClick={() => reveal(trail[k + 1])}
                  sx={{ textTransform: "none", fontFamily: FONT, fontSize: 12, minWidth: 0, px: 0.75, py: 0, maxWidth: 200,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>{t.title}</Button>
              )}
            </React.Fragment>
          ))}
        </Box>
      )}
      {error && <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: "#b42318", px: 2, py: 0.5 }}>{error}</Typography>}

      <Box sx={{ flex: 1, minHeight: 0, display: active === MAIN ? "block" : "none" }}>
        <TutorChat ref={(r) => { refs.current[MAIN] = r; if (mainRef) mainRef.current = r; }} api={mainApi} pendingId={pendingId} context={context} onSource={onSource}
          isClickable={isClickable} labels={labels} quote={quote} onClearQuote={onClearQuote} height="100%"
          anchors={threads.filter((t) => !t.parent && t.kind === "answer")}
          onBranch={(anchor) => branch(MAIN, anchor)} onOpenThread={openThread} onQuoteBranch={onQuoteBranch}
          idle={active !== MAIN} onAnswered={() => markUnread(MAIN)} />
      </Box>
      {threads.filter((t) => mounted.has(t.id)).map((t) => (
        <Box key={t.id} sx={{ flex: 1, minHeight: 0, display: active === t.id ? "block" : "none" }}>
          <TutorChat ref={(r) => { refs.current[t.id] = r; }} api={threadApi(t)} pendingId={`${pendingId}:t${t.id}`} context={context}
            onSource={onSource} isClickable={isClickable} height="100%"
            labels={{ ...labels,
              online: `Side conversation · ${t.title}`,
              empty: `Ask about “${t.quote.length > 120 ? `${t.quote.slice(0, 120)}…` : t.quote}”. The tutor knows where this branched from.`,
            }}
            pinned={{ label: parentLabel(t), quote: t.quote, onClick: () => reveal(t) }}
            anchors={threads.filter((x) => x.parent === t.id)}
            onBranch={(anchor) => branch(t.id, anchor)} onOpenThread={openThread}
            idle={active !== t.id}
            onAnswered={() => { markUnread(t.id); setThreads((cur) => cur.map((x) => (x.id === t.id ? { ...x, exchanges: (x.exchanges || 0) + 1 } : x))); }} />
        </Box>
      ))}

      <Menu open={!!treeAt} anchorEl={treeAt} onClose={() => setTreeAt(null)} PaperProps={{ sx: { maxHeight: 420, width: 320 } }}>
        <MenuItem onClick={() => { setTreeAt(null); activate(MAIN); }} selected={active === MAIN}
          sx={{ fontFamily: FONT, fontSize: 13, gap: 1 }}>
          <ChatBubbleOutlineIcon sx={{ fontSize: 16, color: "#0172A6" }} /> Main conversation
        </MenuItem>
        {tree.map(({ thread: t, depth }) => (
          <MenuItem key={t.id} selected={active === t.id} onClick={() => { setTreeAt(null); openThread(t.id); }}
            sx={{ pl: 2 + depth * 1.75, py: 0.6, alignItems: "flex-start", gap: 0.5, opacity: t.open ? 1 : 0.6 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {t.title}
              </Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: "#6b7280" }}>
                {t.exchanges ? `${t.exchanges} question${t.exchanges === 1 ? "" : "s"}` : "No questions yet"}
                {t.last_at ? ` · ${ago(t.last_at)}` : ""}{t.open ? "" : " · closed"}
              </Typography>
            </Box>
            <IconButton size="small" aria-label={`Rename ${t.title}`}
              onClick={(e) => { e.stopPropagation(); setTreeAt(null); setRenaming({ id: t.id, title: t.title }); }} sx={{ p: 0.25 }}>
              <EditOutlinedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </MenuItem>
        ))}
      </Menu>

      <Dialog open={!!renaming} onClose={() => setRenaming(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: FONT, fontSize: 16 }}>Rename this tab</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth size="small" value={renaming?.title || ""} inputProps={{ maxLength: 120, "aria-label": "Tab name" }}
            onChange={(e) => setRenaming((cur) => ({ ...cur, title: e.target.value }))}
            onKeyDown={(e) => { if (e.key === "Enter" && renaming) { patch(renaming.id, { title: renaming.title.trim() }); setRenaming(null); } }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenaming(null)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" sx={{ textTransform: "none" }}
            onClick={() => { if (renaming) patch(renaming.id, { title: renaming.title.trim() }); setRenaming(null); }}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
